/* eslint-disable max-lines */
import moment, { Moment } from 'moment-timezone'
import { sortBy } from 'lodash-es'
import * as Ticketure from '@/types/ticketure-api.types'
import { getPerksFromMeta } from './api.utils'
import { GLOBAL_CONFIG } from '@/config/environments.config'
import {
  META_EXCLUDE_TICKET_GROUP,
  META_IMAGE_COVER,
  META_IMAGE_PROFILE,
  META_URL_NAME,
} from '@/config/ticketure-metadata.config'
import { TICKETURE_EXTERNAL_MUSIC_EVENT_CATEGORY } from '@/config/ticketure-event-data.config'
import { UTC_FORMAT } from '@/config/dates.config'
import {
  EVENT_CARD_IMAGE_HEIGHT,
  EVENT_CARD_IMAGE_WIDTH,
  EVENT_HEADER_IMAGE_HEIGHT,
  EVENT_HEADER_IMAGE_WIDTH,
} from '@/config/layout.config'
import { transformAdditionalInfoType } from '@/data-access/transformers/transformTicketCategory'

export const DEFAULT_END_TIME_OFFSET = '0h00m'

/**
 * Determine if a value is truthy
 * True values from ticketure can be '1', 'true', 1 or true
 * @param {unknown} value
 * @returns boolean
 */
export const isTruthy = (value: unknown): boolean => (
  String(value) === '1'
  || String(value).toLowerCase() === 'true'
  || value === 1
  || value === true
)

// TODO: move this to date.utils?
/**
 * Checks if the current time is within a validity window defined by optional start and end times.
 *
 * @param validFrom - Start of validity window (ISO timestamp string or null/undefined)
 * @param validTo - End of validity window (ISO timestamp string or null/undefined)
 * @param now - Current time as a Moment object
 * @returns True if current time is within the validity window
 */
export const isInValidWindow = (
  validFrom: Nullable<string>,
  validTo: Nullable<string>,
  now: Moment,
): boolean => {
  // If both bounds are unset, the window is always valid
  if (!validFrom && !validTo) {
    return true
  }

  // Check if the current time is after the start time (if specified)
  const isAfterStart = !validFrom || moment(validFrom).isBefore(now)

  // Check if the current time is before the end time (if specified)
  const isBeforeEnd = !validTo || moment(validTo).isAfter(now)

  // Current time must satisfy both conditions to be in the valid window
  return isAfterStart && isBeforeEnd
}

/**
 * Get event by provided ID
 * @param {Ticketure.EventTemplate | undefined} events - event template list
 * @param {string} id - event id
 */
export const getEventById = (
  events: Ticketure.EventTemplate | undefined,
  id: string,
): Ticketure.EventTemplateDatum | undefined => {
  if (!events?._data) return undefined
  return events._data.find((event) => event.id === id)
}

/**
 * Get Seller by ID
 * @param {Ticketure.Venue | undefined} sellers - seller list or undefined
 * @param {string} id seller id
 * @returns {Ticketure.Venue | undefined} seller or undefined
 */
export const getSellerById = (
  sellers: Ticketure.Seller | undefined,
  id: string,
): Ticketure.SellerDatum | undefined => {
  if (!sellers?._data) return undefined
  return sellers._data.find((seller) => seller.id === id)
}

/**
 * Handle DST offset - add or remove an hour depending on direction
 * @param {moment.Moment} offsetMoment - offset moment object
 * @param {'forward' | 'backward'} direction - which direct to adjust
 */
export const handleDSTOffset = (offsetMoment: moment.Moment, direction: 'forward' | 'backward'): moment.Moment => {
  const nextOffsetMoment = offsetMoment.clone()
  if (direction === 'backward') {
    nextOffsetMoment.subtract(1, 'hour')
  }
  if (direction === 'forward') {
    nextOffsetMoment.add(1, 'hour')
  }

  return nextOffsetMoment
}

/**
 * Given the event start time, create a new moment object with
 * the offset added to or subtracted from the event start time
 * @param {moment.Moment} eventMoment - moment object of the event start time
 * @param {ParsedOffset} offset - parsed offset data
 */
export const offsetEventTime = (eventMoment: moment.Moment, offset: Transformed.ParsedOffset): moment.Moment => {
  const {
    isNegative,
    days,
    hours,
    minutes,
  } = offset

  let offsetMoment: moment.Moment

  if (isNegative) {
    offsetMoment = eventMoment
      .clone()
      .subtract({
        hours,
        days,
        minutes,
      })
  } else {
    offsetMoment = eventMoment
      .clone()
      .add({
        days,
        hours,
        minutes,
      })
  }

  // adjust for DST
  const offsetIsDST = offsetMoment.isDST()
  const eventIsDST = eventMoment.isDST()

  // either the offset or the event is in DST, but not both
  if (offsetIsDST !== eventIsDST) {
    const direction = offsetIsDST ? 'forward' : 'backward'
    offsetMoment = handleDSTOffset(offsetMoment, direction)
  }

  return offsetMoment
}

/**
 * Determine whether both offsets are within range
 *
 * @param {object} args
 * @param {moment.Moment} args.startTime
 * @param {ParsedOffset | undefined} args.startOffset
 * @param {ParsedOffset | undefined} args.endOffset
 * @returns {{ active: boolean, elapsed: boolean}} is the timeslot active, is it elapsed
 */
export const offsetsAreWithinRange = ({
  now,
  startOffset,
  endOffset,
  startTime,
}: {
  startTime: moment.Moment
  now: moment.Moment
  startOffset: moment.Moment | null
  endOffset: moment.Moment | null
}): {
  active: boolean,
  elapsed: boolean,
  isDOS: boolean
} => {
  let startInRange = true
  let endInRange = true
  let isDOS = false

  if (startOffset) {
    startInRange = now.isSameOrAfter(startOffset)
    isDOS = startOffset.isSame(startTime, 'day')
  }

  if (endOffset) {
    endInRange = now.isSameOrBefore(endOffset)
  }

  return {
    active: startInRange && endInRange,
    elapsed: endOffset ? endOffset.isBefore(now) : false,
    isDOS,
  }
}

/**
 * Get sales window data for session
 * @param {object} args
 * @param {string} args.id
 * @param {string} args.startTime
 * @param {ParsedOffsets} args.offsets
 * @param {string} args.timezone
 * @param {moment.Moment} args.now
 * @returns {Transformed.InSalesWindow} in sales window data for session
 */
export const getSalesWindowForSession = ({
  id,
  startTime,
  offsets,
  timezone,
  now,
}: {
  id: string
  startTime: string
  offsets: Transformed.ParsedOffsets
  timezone: string
  now: moment.Moment
}): Transformed.SalesWindow => {
  const {
    parsedEndOffset,
    parsedStartOffset,
  } = offsets

  if (parsedStartOffset || parsedEndOffset) {
    const startMoment = moment.tz(startTime, timezone)
    let startOffset: moment.Moment | null = null
    let endOffset: moment.Moment | null = null

    if (parsedStartOffset) {
      startOffset = offsetEventTime(startMoment, parsedStartOffset)
    }

    if (parsedEndOffset) {
      endOffset = offsetEventTime(startMoment, parsedEndOffset)
    }

    const {
      active,
      elapsed,
      isDOS,
    } = offsetsAreWithinRange({
      startOffset,
      endOffset,
      now,
      startTime: startMoment,
    })

    return {
      id,
      active,
      elapsed,
      isDOS,
      startOffset: startOffset
        ? moment.utc(startOffset, UTC_FORMAT)
          .format()
        : null,
      endOffset: endOffset
        ? moment.utc(endOffset, UTC_FORMAT)
          .format()
        : null,
    }
  }

  // no offsets, it is valid
  return {
    id,
    active: true,
    elapsed: false,
    isDOS: false,
    startOffset: null,
    endOffset: null,
  }
}

/**
 * Given an incoming offset string, get the number
 * example: '12h' -> 12
 * @param value - incoming offset value
 * @param delimiter - delimiter for incoming offset value
 * @returns {number} incoming offset as a number
 */
export const getOffsetNumber = (value: string, delimiter: 'd' | 'h' | 'm'): number => (
  parseInt(value.split(delimiter)[0], 10)
)

/**
 * Parse offset string
 * @param {string} offset offset string e.g. -12h
 * @returns {ParsedOffset} parsed offset
 */
export const parseOffset = (offset: string): Transformed.ParsedOffset => {
  const daysRegex = /\d+d/
  const hoursRegex = /\d+h/
  const minutesRegex = /\d+m/
  const isNegative = offset.indexOf('-') === 0

  const daysMatch = offset.match(daysRegex)
  const days = daysMatch ? getOffsetNumber(daysMatch[0], 'd') : 0

  const hoursMatch = offset.match(hoursRegex)
  const hours = hoursMatch ? getOffsetNumber(hoursMatch[0], 'h') : 0

  const minutesMatch = offset.match(minutesRegex)
  const minutes = minutesMatch ? getOffsetNumber(minutesMatch[0], 'm') : 0

  return {
    days,
    hours,
    minutes,
    isNegative,
  }
}

/**
 * Parse start and end offsets
 * @param {object} args
 * @param {string | null} args.startOffset
 * @param {string | null} args.endOffset
 * @returns {ParsedOffsets} parsed offset or undefined
 */
export const getParsedOffsets = ({
  startOffset,
  endOffset,
}: {
  startOffset: string | null,
  endOffset: string | null,
}): Transformed.ParsedOffsets => {
  let parsedStartOffset: Transformed.ParsedOffset | undefined
  let parsedEndOffset: Transformed.ParsedOffset | undefined

  if (startOffset) {
    parsedStartOffset = parseOffset(startOffset)
  }

  if (endOffset) {
    parsedEndOffset = parseOffset(endOffset)
  }

  return {
    parsedStartOffset,
    parsedEndOffset,
  }
}

/**
 * Sort objects by the displayOrder property
 * @param {T[]} list list of items that may or may not contain the property displayOrder
 * @returns {T[]} if the list is sortable (has property display order) return
 * a sorted list, otherwise unsorted list
 */
export function sortByDisplayOrder<T>(list: T[]): T[] {
  // if displayOrder exists in the first object
  if (list[0] && typeof list[0] === 'object' && 'displayOrder' in list[0]) {
    // cast list as (T & {displayOrder: number})[]
    // and sort
    return sortBy((list as Array<T & { displayOrder: number }>), (item) => item.displayOrder)
  }
  // display order does not exist in T
  return list
}

/**
 * Determines whether the provided metadata list contains an excluded ticket group.
 *
 * @param {Transformed.MetaData[]} metaList - The array of metadata objects to evaluate.
 * @return {boolean} Returns true if the metadata list contains an excluded ticket group, otherwise false.
 */
export function shouldBeExcluded(metaList: Transformed.MetaData[]): boolean {
  return metaList.some((meta) => meta.metakey === META_EXCLUDE_TICKET_GROUP && isTruthy(meta.value))
}

/**
 * Get membership id and perks remaining from meta
 * @param args
 * @param {string} args.identityId user identity id
 * @param {Ticketure.Meta[]} args.meta list of meta
 * @returns {MembershipMeta} membership meta: id & perksRemaining
 */
export const getMembershipMeta = async ({
  identityId,
  meta,
}: {
  identityId: string
  meta: Ticketure.MetaDatum[]
}): Promise<Transformed.MembershipMeta> => {
  const perksRemaining = await getPerksFromMeta(meta)
  return {
    id: identityId,
    perksRemaining,
  }
}

/**
 * Calculates the real price by adding any additional outside fees to the base price.
 *
 * @param {number} price - The base price of the item.
 * @param {{ fee: string, name: string }[] | undefined} outsideFees -
 *   An optional array of fee objects, each containing a `fee` as a string and a `name`.
 *   If undefined or null, no additional fees will be added.
 *
 * @returns {number} The total real price, including any outside fees.
 *
 * @example
 * // Basic usage with fees
 * const price = 100;
 * const fees = [{ fee: '5.00', name: 'Service Fee' }, { fee: '2.50', name: 'Handling Fee' }];
 * const total = getRealPrice(price, fees);
 * // total === 107.5
 *
 * @example
 * // Usage without fees
 * const price = 100;
 * const total = getRealPrice(price, undefined);
 * // total === 100
 */
export const getRealPrice = (
  price: number,
  outsideFees: Array<{ fee: string, name: string }> | undefined,
): number => (
  price + (outsideFees || []).reduce((acc, curr) => acc + parseFloat(curr.fee), 0)
)

/**
 * Transform CalendarPricingResponse into a Record<string, string> object
 * ```
 * {
 *  '2022-01-01': '100.00',
 * }
 * ```
 * @param calendarData
 * @param hasUpFrontFee
 */
export const getCalendarPricingFormatted = (
  calendarData: Ticketure.CalendarPricingResponse,
  hasUpFrontFee: boolean,
): Record<string, string> => calendarData.calendar._data.reduce((acc, dayWithPricing) => {
  // NOTE: pricing data is not always available for every day
  if (
    dayWithPricing.status !== 'available'
    || typeof dayWithPricing.min_price === 'undefined'
  ) return acc

  let price = dayWithPricing.min_price
  const feeValues = typeof dayWithPricing.min_price_fees !== 'undefined'
    ? Object.values(dayWithPricing.min_price_fees).flat()
    : []

  // if there are fees and the event has an up front fee set in meta, add them
  if (feeValues.length > 0 && hasUpFrontFee) {
    price = getRealPrice(parseFloat(dayWithPricing.min_price), feeValues).toString()
  }

  return {
    ...acc,
    [dayWithPricing.date]: price,
  }
}, {} as Record<string, string>)

/**
 * Create print link using order id
 * @param {string} orderId order id
 * @returns {string} print link
 */
export const createPrintLink = (orderId: string): string => (
  `${GLOBAL_CONFIG.PROVIDER_API_URL}/api/ticket_order/${orderId}/html`)

/**
* Checks to see if the incoming category string contains the MusicExternal EventCategory
* @param {string} category incoming cateogry string
* @returns {boolean}
*/
export const isExternalEventCategory = (category: string): boolean => (
  (category.toLowerCase()).includes(TICKETURE_EXTERNAL_MUSIC_EVENT_CATEGORY.toLowerCase())
)

/**
 * Get event slug from meta data
 * @param {MetaData[]} meta incoming meta data
 * @param {string} eventId event id
 * @returns {string} event slug
 */
export const getEventSlugFromMeta = (meta: Transformed.MetaData[], eventId: string): string => (
  meta.find((m) => m.resourceId === eventId && m.metakey === META_URL_NAME)?.value || ''
)

/**
 * Wrap any iframes in the string with the responsive-iframe-wrapper
 * NOTE: As of June 10th, 2024, if an event is created in the Events app without a description
 * we get null or undefined from the api if the event is created without a description
 * in the ticketure app, we get an empty string. As such, we need to handle a potential null or
 * undefined value and will fall back to the default string
 * @param {string} incomingString incoming string
 * @param defaultString
 * @returns string with wrapped iframes
 */
export const wrapIframesWithResponsiveDiv = (
  incomingString: string | undefined | null,
  defaultString = '',
): string => {
  if (!incomingString) return defaultString
  return incomingString.replaceAll(/(<iframe.*<\/iframe>)/g, '<div class="responsive-iframe-wrapper">$1</div>')
}

/**
 * Checks if the metadata list contains an active "up front fee" flag.
 *
 * NOTE: in 2.0 this should be saved in state when we first fetch the seller
 * Then passed to calls that need it.
 *
 * @returns {boolean} True if the metadata contains an "up front fee" flag set to a truthy value; otherwise, false.
 * @param metaData
 */
export const hasLocalCategoriesInMeta = (metaData: Transformed.MetaData[]): boolean => (
  metaData.some((metaItem) => (
    metaItem.resource === 'ticket_group'
    && metaItem.metakey === 'local'
    && isTruthy(metaItem.value)
  ))
)

/**
 * Get remaining capacity for a session
 * @param entity
 * @return {number} remaining capacity
 */
export const getRemainingCapacity = (entity: Ticketure.EventSessionDatum | Ticketure.TicketGroupDatum): number => (
  entity.capacity <= 0
    ? entity.capacity
    : entity.capacity - (entity.used_capacity || 0)
)

/**
 * Finds the closest offset to the current time from an array of offsets.
 *
 * @param {string[]} offsets - An array of offsets to compare.
 * @param {moment.Moment} now - the current time.
 * @returns {string} - The closest offset to the current time.
 */
export const getClosestOffsetToNow = (offsets: string[], now: moment.Moment): string | null => {
  if (!offsets || offsets.length === 0) {
    return null
  }
  return offsets
    .reduce((closest, startOffset) => {
      const currentDiff = Math.abs(moment.utc(startOffset).diff(now))
      const closestDiff = Math.abs(moment.utc(closest).diff(now))
      return currentDiff < closestDiff ? startOffset : closest
    })
}

/**
 * Get event images, if no images use a placeholder
 * TODO: move to api - https://app.asana.com/1/23646394350278/project/1206566497440920/task/1210618052534623?focus=true
 * - cover
 * - profile
 */
export const getEventImages = (meta: readonly Transformed.MetaData[]): {
  eventCard: string | null,
  eventHeader: string | null,
} => {
  const eventCard = meta.find((m) => m.metakey === META_IMAGE_PROFILE)
  const eventHeader = meta.find((m) => m.metakey === META_IMAGE_COVER)

  return {
    eventCard:
      (eventCard
        && eventCard.value
        && `${eventCard.value}?shrinkonly&sizex=${EVENT_CARD_IMAGE_WIDTH}&sizey=${EVENT_CARD_IMAGE_HEIGHT}`)
      || `https://placehold.co/${EVENT_CARD_IMAGE_WIDTH}x${EVENT_CARD_IMAGE_HEIGHT}.png?text=_`,
    eventHeader: (eventHeader
        && eventHeader.value
        && `${eventHeader.value}?shrinkonly&sizex=${EVENT_HEADER_IMAGE_WIDTH}&sizey=${EVENT_HEADER_IMAGE_HEIGHT}`)
      || `https://placehold.co/${EVENT_HEADER_IMAGE_WIDTH}x${EVENT_HEADER_IMAGE_HEIGHT}.png?text=_`,
  }
}
/**
 * Get typed visibility from incoming string type
 * @param {string} hiddenType hidden type from ticketure
 * @returns {TicketVisibility} typed visibility
 */
export const getTicketVisibility = (hiddenType: string): Transformed.TicketVisibility => {
  switch (hiddenType) {
  case 'public_member_only':
    return 'MEMBER'
  case 'public_link_only':
  case 'public_browsable':
    return 'PUBLIC'
  default:
    return 'PRIVATE'
  }
}
/**
 * Transforms Ticketure Additional Info Spec response into in a structured array of AdditionalInfo objects.
 *
 * @param {Ticketure.AdditionalInfoSpec | null} incomingAdditionalInfo - The additional information specification
 * to be transformed.
 * @param {string} categoryId - The category ID associated with the additional information.
 * @returns {AdditionalInfo[] | null} - An array of AdditionalInfo objects representing the transformed
 * additional information, or null if no information is provided.
 *
 * @typedef {Object} AdditionalInfo - Represents additional information associated with a ticket category.
 */
export const transformAdditionalInfo = (
  incomingAdditionalInfo: Ticketure.AdditionalInfoSpec | null,
  categoryId: string,
): Transformed.AdditionalInfo[] | null => {
  let additionalInfo: Transformed.AdditionalInfo[] | null = null

  if (incomingAdditionalInfo) {
    const properties = Object.entries(incomingAdditionalInfo.properties)

    const formatted = properties.map((el): Transformed.AdditionalInfo | null => {
      const [
        id,
        {
          description,
          type,
          propertyOrder,
          title,
          enum: options,
        },
      ] = el

      const transformedType = transformAdditionalInfoType(type, options)

      if (!transformedType) {
        return null
      }

      return {
        id: `${categoryId}|${id}`,
        key: id,
        title,
        description,
        type: transformedType,
        required: incomingAdditionalInfo.required?.includes(id) || false,
        order: propertyOrder,
        options,
      }
    })
      .filter((item) => item)

    if (formatted.length > 0) {
      additionalInfo = formatted as Transformed.AdditionalInfo[]
    }
  }

  return additionalInfo
}

/**
 * Checks if a promise is fulfilled
 * @param input
 */
export const isFulfilled = <T>(
  input: PromiseSettledResult<T>,
): input is PromiseFulfilledResult<T> => (input.status === 'fulfilled' && !!input.value)

/**
 * Determines the visibility level for a given event based on its hidden type.
 *
 * @param {Ticketure.EventTemplateDatum} event - The event for which visibility is to be determined.
 * @returns {Transformed.EventVisibility} The visibility level of the event.
 *    - Returns 'MemberOnly' if the event's hidden type is 'public_member_only'.
 *    - Returns 'AdminOnly' if the event's hidden type includes 'cms_'.
 *    - Returns 'Public' for all other cases.
 */
export const getVisibilityForEvent = (event: Ticketure.EventTemplateDatum): Transformed.EventVisibility => {
  if (event.hidden_type === 'public_member_only') {
    return 'MemberOnly'
  }

  if (event.hidden_type.includes('cms_')) {
    return 'AdminOnly'
  }

  return 'Public'
}

/**
 * Helper function to create a list with _data, _count, and _total properties.
 */
export const toListEnvelope = <T>(list: T[]): { _data: T[], _count: number, _total: number } => ({
  _data: list,
  _count: list.length,
  _total: list.length,
})
